'use client';

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useRouter } from 'next/navigation';
import { useState } from 'react';
import { cleanPhoneNumber } from '@/services/phone';
import {
  signIn,
  verifyMfaLoginOtp,
  resendMfaLoginOtp
} from '@/services/webApi';
import { emailSchema } from '@goodneighbor/api/schemas/auth';
import { MfaLoginState } from './useAuthHooks';

export type PartnerLoginInput = {
  email: string;
  password: string;
};

export function usePartnerLogin() {
  const queryClient = useQueryClient();
  const router = useRouter();
  const [mfaLoginState, setMfaLoginState] = useState<MfaLoginState | null>(
    null
  );

  const signInMutation = useMutation({
    mutationFn: async ({ email, password }: PartnerLoginInput) => {
      const isEmail = emailSchema.safeParse(email).success;
      const phoneOrEmail = isEmail
        ? email.toLowerCase()
        : cleanPhoneNumber(email);

      return signIn({
        phoneOrEmail,
        password,
        intent: 'partner'
      });
    },
    onSuccess: (response) => {
      if (response && !('data' in response)) {
        setMfaLoginState({
          mfaToken: response.mfaToken,
          mfaMethod: response.mfaMethod as 'EMAIL' | 'SMS',
          maskedContact: response.maskedContact
        });
        return;
      }
      queryClient.removeQueries({ queryKey: ['account'] });
      router.replace('/partner/dashboard');
    }
  });

  const verifyMfaLoginMutation = useMutation({
    mutationFn: ({ otp, mfaToken }: { otp: string; mfaToken: string }) =>
      verifyMfaLoginOtp({ otp }, mfaToken),
    onSuccess: () => {
      setMfaLoginState(null);
      queryClient.removeQueries({ queryKey: ['account'] });
      router.replace('/partner/dashboard');
    }
  });

  const resendMfaLoginMutation = useMutation({
    mutationFn: (mfaToken: string) => resendMfaLoginOtp(mfaToken),
    onSuccess: (response) => {
      if (response && 'mfaToken' in response) {
        setMfaLoginState((prev) =>
          prev ? { ...prev, mfaToken: response.mfaToken } : null
        );
      }
    }
  });

  const handleSubmit = (data: PartnerLoginInput) => {
    signInMutation.mutate(data);
  };

  const handleMfaCancel = () => {
    setMfaLoginState(null);
  };

  return {
    signInMutation,
    verifyMfaLoginMutation,
    resendMfaLoginMutation,
    handleSubmit,
    mfaLoginState,
    handleMfaCancel
  };
}
